<script lang="ts" setup>
import { useFollow } from "@/stores/follow";
import { useGlobalUser } from "@/stores/globalUser";
import { usePopular } from "@/stores/popular";
import { useSearch } from "@/stores/search";
import { Popup, showNotify, Tab, Tabs } from "vant";
import { nextTick, onMounted, ref, UnwrapRef } from "vue";

import MomentActions from "@/components/mobile/popup/MomentActions.vue";
import SocialListItem from "@/components/mobile/SocialListItem.vue";
import ThreadItem from "@/components/mobile/ThreadItem.vue";
import ThreadList from "@/components/mobile/ThreadList.vue";
import { onBeforeRouteLeave, useRouter } from "vue-router";

const props = defineProps<{
  show: boolean;
}>();

const emit = defineEmits<{
  (e: "update:show", isShow: boolean): void;
}>();

const globalUser = useGlobalUser();
const matePopularStore = usePopular();
const searchStore = useSearch();
const followStore = useFollow();
const router = useRouter();

const searchRef = ref<HTMLTextAreaElement | null>(null);
const isSearch = ref<boolean>(false);
const showActions = ref<boolean>(false);
const keyword = ref<string>("");

onMounted(() => {
  nextTick(() => {
    searchRef.value?.focus();
  });
});

// EventHandler
const onFollowClick = async (
  isFollowing: boolean,
  item: MatePopular
): Promise<number | null> => {
  try {
    if (isFollowing && item.mateFollowerId) {
      await followStore.unfollow(item.mateFollowerId);
      item.mateFollowerId = null;
      item.isFollowing = false;
      return null;
    } else {
      const record = await followStore.follow(item.id);
      item.mateFollowerId = record.id!;
      item.isFollowing = true;
      return record.id!;
    }
  } catch (e) {
    showNotify({ type: "danger", message: e.message });
    throw e;
  }
};

// popup
const toOperateMoment = ref<Moment | null>(null);
const onActionClick = (moment: UnwrapRef<Moment>) => {
  toOperateMoment.value = moment;
  showActions.value = true;
};

onBeforeRouteLeave(() => {
  emit("update:show", false);
});
</script>
<template>
  <popup
    :show="show"
    class="flex flex-col w-full h-[90%] gap-2"
    position="bottom"
    round
    @update:show="emit('update:show', $event)"
  >
    <div class="p-4 pt-6 navbar bg-base-100 h-top-nav gap-4">
      <div class="w-full block rounded-lg overflow-hidden">
        <SearchInput @onSearch="emit('update:show', false)" />
      </div>
    </div>
    <div v-if="isSearch" class="w-full overflow-hidden relative">
      <tabs
        v-model:active="searchStore.tabIdx"
        class="h-full thread-tabs grid grid-rows-[auto_1fr]"
        swipeable
      >
        <tab
          :title="`${$t('Moment.character')} ${
            searchStore.mateKeyList.total
              ? `(${searchStore.mateKeyList.total})`
              : ''
          }`"
        >
          <!-- :on-scroll-load="searchStore.fetchOlderPromotedList" -->
          <thread-list
            v-if="searchStore.mateList.length > 0"
            ref="promptListRef"
            :enable-pull-load="false"
            :on-scroll-load="() => searchStore.fetchMoreMateList(keyword)"
            class="px-2"
            @on-scroll="(h) => (searchStore.mateKeyList.scrollTop = h)"
          >
            <social-list-item
              v-for="item in searchStore.mateList"
              :id="item.id"
              :key="item.id"
              v-nsfw="item.isNsfw"
              :avatar-url="item.mediaUrl"
              :content="item.bio"
              :follow-click="() => onFollowClick(item.isFollowing, item)"
              :gender="item.gender"
              :is-following="item.isFollowing"
              :mate-id="item.id"
              :name="item.name"
              :type="item.type"
              class="px-4 relative"
              @click="
                $router.push({
                  name: 'mateHome',
                  params: { uuid: item.uuid },
                })
              "
            />
          </thread-list>
          <div
            v-if="!matePopularStore.init"
            class="h-[200px] flex items-center justify-center"
          >
            <span class="loading"></span>
          </div>
          <div
            v-else
            class="flex flex-col items-center justify-center h-[200px] w-full"
          >
            <div class="flex flex-col items-center justify-center">
              <div class="text-2xl font-bold">
                {{ $t("Message.noCharactersAvailable") }}
              </div>
              <div class="text-neutral-400">
                {{ $t("Message.noMatchingRole") }}
              </div>
            </div>
          </div>
        </tab>
        <tab
          :title="`${$t('Moment.moment')} ${
            searchStore.momentKeyList.total
              ? `(${searchStore.momentKeyList.total})`
              : ''
          }`"
        >
          <thread-list
            v-if="searchStore.momentList.length > 0"
            ref="followingListRef"
            :enable-pull-load="false"
            :on-scroll-load="() => searchStore.fetchMoreMomentList(keyword)"
            class="px-2"
            @on-scroll="(h) => (searchStore.momentKeyList.scrollTop = h)"
          >
            <!-- @reaction-click="onActionClick(item)" -->
            <thread-item
              v-for="item in searchStore.momentList"
              :key="item.uuid"
              :avatar-url="item.mate.mediaUrl"
              :chat-message-list="item.chatMessagezz"
              :content="item.text"
              :enable-reaction-btn="globalUser.isLogin"
              :from-mate="item.fromMate"
              :has-next="item.childId != null"
              :has-prev="item.parentId != null"
              :impression-reactions="[]"
              :mate-id="item.mate.uuid"
              :mate-tone-id="item.mate.mateToneId"
              :media-list="item.mediazz"
              :mention-matezz="item.mentionMatezz"
              :moment="item"
              :reply="item.reply"
              :time="item.dtCreate"
              :title="item.mate.name"
              :type="item.mate.type"
              :uuid="item.uuid"
              :visibility="item.visibility"
              enable-audio-play
              enable-show-more-link
              @reaction-click="onActionClick(item)"
              @thread-click="
                $router.push({
                  name: 'threadDetail',
                  params: { uuid: item.uuid },
                })
              "
            />
          </thread-list>
          <div
            v-if="!matePopularStore.init"
            class="h-[200px] flex items-center justify-center"
          >
            <span class="loading"></span>
          </div>
          <div
            v-else
            class="flex flex-col items-center justify-center h-[200px] w-full"
          >
            <div class="flex flex-col items-center justify-center">
              <div class="text-2xl font-bold">
                {{ $t("Message.noMomentsYet") }}
              </div>
              <div class="text-neutral-400">
                {{ $t("Message.storyNoMoment") }}
              </div>
            </div>
          </div>
        </tab>
      </tabs>
    </div>

    <div v-else class="h-full overflow-hidden relative flex flex-col gap-2">
      <div class="flex flex-col gap-2 px-4">
        <div class="font-semibold text-xl">{{ $t("Titles.trendingTags") }}</div>
        <HotMateTags
          @onTagSelect="
            router.push({
              name: 'search',
              query: {
                q: $event,
              },
            })
          "
        />
      </div>
      <div>
        <div class="px-4 text-xl font-semibold">
          {{ $t("Message.hotCharater") }}
        </div>
        <div class="h-full overflow-y-scroll pb-10">
          <div class="w-full relative">
            <div
              v-if="matePopularStore.list.length > 0"
              class="flex flex-col w-full"
            >
              <social-list-item
                v-for="item in matePopularStore.list"
                :id="item.id"
                :key="item.id"
                :avatar-url="item.mediaUrl"
                :content="item.bio"
                :follow-click="() => onFollowClick(item.isFollowing, item)"
                :gender="item.gender"
                :is-following="item.isFollowing"
                :mate-id="item.id"
                :name="item.name"
                :type="item.type"
                class="px-4"
                @click="
                  $router.push({
                    name: 'mateHome',
                    params: { uuid: item.uuid },
                  })
                "
              />
            </div>
            <div
              v-else-if="!matePopularStore.init"
              class="h-[200px] flex items-center justify-center"
            >
              <span class="loading"></span>
            </div>
            <EmptyContent
              v-else
              :desc="$t('Message.noRecommendedRoles')"
              :title="$t('Message.noWelcomeMoment')"
            />
          </div>
        </div>
      </div>
    </div>
    <moment-actions v-model:show="showActions" :moment="toOperateMoment" />
  </popup>
</template>
